// src/componentes/Propiedades/EditarPropiedad.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { doc, getDoc, updateDoc, Timestamp } from "firebase/firestore";
import { db } from "../../Firebase/firebaseconfig";
// CSS correcto (antes importaba el componente por error)
import "../NuevoCliente/NuevoCliente.css";
import { listProyectos } from "../../services/proyectosService";
import { buildPropSearchFields } from "../../helpers/propIndex";
import notify from "../common/notify";

const EditarPropiedad = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    folio: "",
    nombre: "",
    tipo: "",
    ubicacion: "",
    metros2: "",
    precioContado: "",
    precioFinanciado: "",
    estado: "",
    notas: "",
    // campos que ayudan al reindexado:
    fraccionamientoId: "",
    fraccNombre: "",
    fraccionamientoNombre: "",
    manzana: "",
    lote: "",
    codigoHumano: "",
    proyectoId: "",
    proyectoNombre: "",
  });

  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [proyectos, setProyectos] = useState([]);

  // Carga los datos existentes
  useEffect(() => {
    const fetchProp = async () => {
      try {
        const ref = doc(db, "propiedades", id);
        const snap = await getDoc(ref);
        if (!snap.exists()) {
          setError("No se encontró la propiedad.");
        } else {
          const data = snap.data();
          setForm({
            folio: data.folio || "",
            nombre: data.nombre || "",
            tipo: data.tipo || "",
            ubicacion: data.ubicacion || "",
            metros2: data.metros2?.toString?.() || "",
            precioContado: data.precioContado?.toString?.() || "",
            precioFinanciado: data.precioFinanciado?.toString?.() || "",
            estado: data.estado || "",
            notas: data.notas || "",
            // para índice
            fraccionamientoId: data.fraccionamientoId || "",
            fraccNombre: data.fraccNombre || data.fraccionamientoNombre || "",
            fraccionamientoNombre: data.fraccionamientoNombre || data.fraccNombre || "",
            manzana: (data.manzana ?? data.manzanaInt ?? "").toString(),
            lote: (data.lote ?? data.loteInt ?? "").toString(),
            codigoHumano: data.codigoHumano || "",
            proyectoId: data.proyectoId || "",
            proyectoNombre: data.proyectoNombre || "",
          });
        }
      } catch (err) {
        console.error(err);
        setError("Error al cargar la propiedad.");
      } finally {
        setLoading(false);
      }
    };
    fetchProp();
  }, [id]);

  useEffect(() => {
    const fetchProyectos = async () => {
      try {
        const data = await listProyectos();
        setProyectos(data);
      } catch (err) {
        console.error("Error cargando proyectos:", err);
      }
    };
    fetchProyectos();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    const { nombre, tipo, ubicacion, precioContado, precioFinanciado } = form;
    if (!nombre || !tipo || !ubicacion || !precioContado || !precioFinanciado) {
      notify.error("Completa todos los campos obligatorios.");
      return;
    }

    // ── Validación coherencia precio (ES2-3) ─────────────────────────
    const pc = Number(precioContado || 0);
    const pf = Number(precioFinanciado || 0);
    if (pc <= 0 || pf <= 0) {
      notify.error("Los precios deben ser mayores a cero.");
      return;
    }
    if (pc > pf) {
      notify.error(
        `Precio inconsistente: contado ($${pc.toLocaleString()}) no puede ser MAYOR que financiado ($${pf.toLocaleString()}).`,
      );
      return;
    }

    setIsSubmitting(true);
    try {
      const ref = doc(db, "propiedades", id);

      // Normaliza numéricos
      const metros2Num = parseFloat(form.metros2) || 0;
      const precioContadoNum = parseFloat(form.precioContado) || 0;
      const precioFinanciadoNum = parseFloat(form.precioFinanciado) || 0;
      const codigoHumano = `MZ${form.manzana}-LT${form.lote}`;

      // Construimos el objeto base para reindexar
      const baseParaIndice = {
        folio: form.folio,
        nombre: form.nombre,
        tipo: form.tipo,
        ubicacion: form.ubicacion,
        metros2: metros2Num,
        precioContado: precioContadoNum,
        precioFinanciado: precioFinanciadoNum,
        estado: form.estado,
        notas: form.notas,
        // estos ayudan al indexador a incluir MZ/LT y el nombre del fracc
        fraccionamientoId: form.fraccionamientoId,
        fraccNombre: form.fraccNombre,
        fraccionamientoNombre: form.fraccionamientoNombre,
        manzana: form.manzana,
        lote: form.lote,
        codigoHumano,
        proyectoId: form.proyectoId || "",
        proyectoNombre: form.proyectoNombre || "",
      };

      // 🔥 Recalcular índices (buscadorNgram, buscadorTokens, ints, claveLote, etc.)
      const idx = buildPropSearchFields(baseParaIndice);

      await updateDoc(ref, {
        ...baseParaIndice,
        ...idx,
        actualizado: Timestamp.now(), // estandarizar campo de timestamp
      });

      navigate("/app/propiedades", { replace: true });
    } catch (err) {
      console.error(err);
      notify.error("No se pudo actualizar la propiedad.");
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return <div className="spinner">Cargando datos...</div>;
  }
  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="nuevo-cliente-container">
      <h2>Editar Propiedad</h2>
      <form onSubmit={handleSubmit} className="form-cliente">
        <input name="folio" value={form.folio} disabled placeholder="Folio" />
        <input
          name="nombre"
          placeholder="Nombre"
          value={form.nombre}
          onChange={handleChange}
          required
        />
        <input
          name="tipo"
          placeholder="Tipo (ej. Terreno, Casa)"
          value={form.tipo}
          onChange={handleChange}
          required
        />
        <input
          name="ubicacion"
          placeholder="Ubicación"
          value={form.ubicacion}
          onChange={handleChange}
          required
        />
        <input
          name="metros2"
          type="number"
          placeholder="Metros cuadrados"
          value={form.metros2}
          onChange={handleChange}
        />
        <input
          name="precioContado"
          type="number"
          placeholder="Precio contado en MXN"
          value={form.precioContado}
          onChange={handleChange}
          required
        />
        <input
          name="precioFinanciado"
          type="number"
          placeholder="Precio financiado en MXN"
          value={form.precioFinanciado}
          onChange={handleChange}
          required
        />
        <input
          name="estado"
          placeholder="Estado (ej. disponible, vendido)"
          value={form.estado}
          onChange={handleChange}
        />
        <select
          value={form.proyectoId ? `${form.proyectoId}|${form.proyectoNombre}` : ""}
          onChange={(e) => {
            const [proyectoId, proyectoNombre] = e.target.value.split("|");
            setForm((prev) => ({
              ...prev,
              proyectoId: proyectoId || "",
              proyectoNombre: proyectoNombre || "",
            }));
          }}
        >
          <option value="">Proyecto relacionado (opcional)</option>
          {proyectos.map((p) => (
            <option key={p.id} value={`${p.id}|${p.nombre}`}>
              {p.nombre}
            </option>
          ))}
        </select>
        <textarea
          name="notas"
          placeholder="Notas adicionales"
          value={form.notas}
          onChange={handleChange}
        />

        
        
        <div style={{ display: "flex", gap: 8 }}>
          <input
            name="manzana"
            type="number"
            placeholder="Manzana"
            value={form.manzana}
            onChange={handleChange}
          />
          <input
            name="lote"
            type="number"
            placeholder="Lote"
            value={form.lote}
            onChange={handleChange}
          />
        </div>
        

        <div style={{ display: "flex", justifyContent: "space-between", marginTop: "1rem" }}>
          <button
            type="button"
            className="secondary"
            onClick={() => navigate(-1)}
            disabled={isSubmitting}
          >
            Cancelar
          </button>
          <button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Guardando..." : "Guardar Cambios"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditarPropiedad;
